/** Iter 45 — screen format binding helpers. Pure, no React. */

export const BINDING_STATUSES = ["bound", "ambiguous", "unresolved"];
export const BINDING_OPERATIONS = ["exfmt", "write", "read", "readc", "unknown"];

export const STATUS_META = {
  bound: { label: "Bound", bg: "#defbe6", fg: "#0e6027", border: "#6fdc8c", wording: "observed screen format binding" },
  ambiguous: { label: "Ambiguous", bg: "#fff8e1", fg: "#8a6d00", border: "#d2a106", wording: "ambiguous format name" },
  unresolved: { label: "Unresolved", bg: "#f4f4f4", fg: "#525252", border: "#c6c6c6", wording: "unresolved format usage" },
};

export const OPERATION_META = {
  exfmt: { label: "EXFMT", hint: "Program statement that displays the format and reads it back." },
  write: { label: "WRITE", hint: "Program statement that writes (displays) the format." },
  read: { label: "READ", hint: "Program statement that reads the format." },
  readc: { label: "READC", hint: "Program statement that reads changed subfile records from the format." },
  unknown: { label: "UNKNOWN", hint: "Operation could not be classified." },
};

export const statusMeta = (status) => STATUS_META[status] || STATUS_META.unresolved;
export const operationLabel = (op) => (OPERATION_META[op] || OPERATION_META.unknown).label;

/** Iter 48 — parser-proven vs consultant-supplied bindings. */
export const BINDING_ORIGINS = ["parser", "manual"];
export const ORIGIN_META = {
  parser: { label: "Parser-proven", bg: "#defbe6", fg: "#0e6027", border: "#6fdc8c", hint: "Observed RPG statement matched to the parsed DSPF that defines the format." },
  manual: { label: "Manual", bg: "#fff1f1", fg: "#a2191f", border: "#ffb3b8", hint: "Manual binding supplied by consultant — not parser-proven. Used for walkthrough and field reference assignment." },
};
export const MANUAL_WARNING = "Manual binding supplied by consultant — not parser-proven. Used for walkthrough and field reference assignment.";
export const originMeta = (origin) => ORIGIN_META[origin] || ORIGIN_META.parser;
export const isManualBinding = (b) => Boolean(b && (b.origin === "manual" || b.manual === true));
export const isInactiveBinding = (b) => Boolean(b && b.active === false);

/** Validation for the manual binding form — mirrors the API's 400s so the consultant sees them before submitting. */
export function validateManualBinding(form) {
  const errors = {};
  for (const k of ["program_name", "display_file", "record_format"]) {
    const v = (form[k] || "").trim();
    if (!v) errors[k] = "required";
    else if (v.length > 10) errors[k] = "max 10 characters";
  }
  if (!BINDING_OPERATIONS.includes(form.operation)) errors.operation = "pick an operation";
  if (!["high", "medium", "low"].includes(form.confidence)) errors.confidence = "pick a confidence";
  if (!(form.manual_note || "").trim()) errors.manual_note = "explain why this program uses this screen format";
  return errors;
}

/** Fields to send; names upper-cased, note trimmed. */
export function manualBindingPayload(form) {
  return {
    program_library: (form.program_library || "").trim().toUpperCase() || null,
    program_name: (form.program_name || "").trim().toUpperCase(),
    display_file: (form.display_file || "").trim().toUpperCase(),
    record_format: (form.record_format || "").trim().toUpperCase(),
    operation: form.operation,
    confidence: form.confidence,
    manual_note: (form.manual_note || "").trim(),
  };
}

/** Program column wording for a binding row — never pretends certainty. */
export function bindingTarget(b) {
  if (!b) return "";
  if (b.status === "bound" && b.display_file) return `${b.display_file}/${b.record_format}`;
  if (b.status === "ambiguous") return `${b.record_format} · ambiguous format name`;
  return `${b.record_format} · unresolved format usage`;
}

/** Whether a field-reference row inherited its program from a proven binding (Iter 45). */
export const inheritedViaBinding = (row) => Boolean(row?.display_file && row?.program_name && /screen format binding/.test(row.usage_context || ""));

/** Iter 48 — program inherited from a consultant-supplied manual binding (not parser-proven). */
export const inheritedViaManualBinding = (row) => Boolean(row?.display_file && row?.program_name && /manual screen format binding/.test(row.usage_context || ""));

/** Whether a screen field-reference row could not inherit a program because of an ambiguous format name. */
export const blockedByAmbiguousFormat = (row) => Boolean(row?.display_file && !row?.program_name && /ambiguous format name/.test(row.usage_context || ""));

/** "1 bound (1 high) · 0 ambiguous · 2 unresolved" (+ " · 1 manual" when consultants supplied bindings) */
export function bindingSummaryLine(s) {
  if (!s || !s.bindings) return "";
  const manual = s.manual ? ` · ${s.manual} manual (consultant supplied, not parser-proven)` : "";
  return `${s.bound} bound (${s.high} high) · ${s.ambiguous} ambiguous format name${s.ambiguous === 1 ? "" : "s"} · ${s.unresolved} unresolved format usage${s.unresolved === 1 ? "" : "s"}${manual}`;
}
